import React from "react";
import { Alert, AlertDescription } from "../ui/alert";
import { ssrFormattedDateOr } from "../../lib/ssrFormattedDate";
import type { BillingSummary, RouteMap } from "../../pages/MainAppPage/types";

type BillingBannerProps = {
  billing?: BillingSummary | null;
  billingRoute: RouteMap["billing"];
};

const BILLING_STATUS_TRIALING = "trialing";
const BILLING_STATUS_ACTIVE = "active";

function hasAnySubscription(billing: BillingSummary): boolean {
  return Boolean(billing.hasSubscription ?? billing.hasStripeSubscription);
}

export const BillingBanner: React.FC<BillingBannerProps> = ({
  billing,
  billingRoute,
}) => {
  if (!billing) return null;

  const accessSuspended = Boolean(billing.restricted);
  const onActiveTrial =
    billing.status === BILLING_STATUS_TRIALING &&
    billing.showTrialEndsInUi === true &&
    !accessSuspended;

  const periodEndDisplay = ssrFormattedDateOr(
    billing.subscriptionPeriodEndAtDisplay,
    "",
  ).trim();

  const showScheduledCancellationNotice =
    !accessSuspended &&
    billing.subscriptionCancelAtPeriodEnd === true &&
    periodEndDisplay.length > 0 &&
    hasAnySubscription(billing) &&
    (billing.status === BILLING_STATUS_ACTIVE ||
      billing.status === BILLING_STATUS_TRIALING);

  if (!accessSuspended && !onActiveTrial && !showScheduledCancellationNotice) {
    return null;
  }

  return (
    <div className="mt-4 flex flex-col gap-2">
      {accessSuspended ? (
        <Alert variant="error">
          <AlertDescription>
            Your website and quote access are suspended until payment is active.{" "}
            <a href={billingRoute} className="underline font-medium">
              Add payment details
            </a>{" "}
            to restore access.
          </AlertDescription>
        </Alert>
      ) : null}

      {onActiveTrial ? (
        <Alert variant="warning" role="status" className="text-center">
          <AlertDescription>
            <span>You&apos;re on a free trial.</span>
            {billing.trialEndsAtDisplay ? (
              <span className="font-semibold">
                {" "}
                Free trial access ends {billing.trialEndsAtDisplay} (your
                business time).
              </span>
            ) : null}{" "}
            <a
              href={billingRoute}
              className="font-semibold underline underline-offset-2"
            >
              View billing
            </a>{" "}
            to manage your plan or add a payment method.
          </AlertDescription>
        </Alert>
      ) : null}

      {showScheduledCancellationNotice ? (
        <Alert variant="warning" role="status" className="text-center">
          <AlertDescription>
            Your subscription is set to cancel. Website and quote access
            continue through{" "}
            <span className="font-semibold">{periodEndDisplay}</span>, then
            pause until you subscribe again.{" "}
            <a
              href={billingRoute}
              className="font-semibold underline underline-offset-2"
            >
              View billing
            </a>{" "}
            to change or renew your plan.
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
};
